// @ts-nocheck
/**
 * Shared Forgot Password / Reset Password Form
 *
 * Used by all 3 login flows:
 *   - Advisor Portal  (/forgot-password)      → variant="light"
 *   - Admin Portal    (/admin/reset-password)  → variant="dark"
 *   - Client Portal   (/portal/forgot-password)→ variant="light"
 *
 * Modern split-panel layout matching the login screens:
 *   Left: branding panel with gradient background
 *   Right: 3-step form (email → code+password → success)
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle2, Eye, EyeOff, KeyRound, Shield, Mail } from 'lucide-react'

type Step = 'email' | 'code' | 'success'

export interface ForgotPasswordFormProps {
  /** Display name shown in the form heading */
  portalName: string
  /** Logo image URL for the branding panel & mobile header */
  logoUrl?: string
  /** Alt text for the logo image */
  logoAlt?: string
  /** URL to navigate back to the login page */
  loginUrl: string
  /** Minimum password length (admin: 12, others: 8) */
  minPasswordLength?: number
  /** Email placeholder text */
  emailPlaceholder?: string
  /** Callback to send the verification code */
  onSendCode: (email: string) => Promise<void>
  /** Callback to confirm code + new password */
  onResetPassword: (email: string, code: string, newPassword: string) => Promise<void>
  /** Optional: Callback to resend the code (defaults to calling onSendCode again) */
  onResendCode?: (email: string) => Promise<void>
  /** Visual variant — matches corresponding login page */
  variant?: 'light' | 'dark'
  /** Left panel heading content */
  brandingHeadline?: React.ReactNode
  /** Left panel subtitle */
  brandingSubtitle?: string
  /** Left panel badge text (e.g. "Restricted Access") */
  brandingBadge?: string
  /** Left panel badge icon */
  brandingBadgeIcon?: React.ReactNode
  /** Footer note below the form */
  footerNote?: string
}

export default function ForgotPasswordForm({
  portalName,
  logoUrl,
  logoAlt,
  loginUrl,
  minPasswordLength = 8,
  emailPlaceholder = 'you@email.com',
  onSendCode,
  onResetPassword,
  onResendCode,
  variant = 'light',
  brandingHeadline,
  brandingSubtitle,
  brandingBadge,
  brandingBadgeIcon,
  footerNote,
}: ForgotPasswordFormProps) {
  const navigate = useNavigate()
  const isDark = variant === 'dark'

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // ── Step 1: Send code ──
  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await onSendCode(email)
      setStep('code')
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2: Reset password ──
  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password.length < minPasswordLength) {
      setError(`Password must be at least ${minPasswordLength} characters`)
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (code.length !== 6) {
      setError('Verification code must be 6 digits')
      return
    }

    setLoading(true)
    try {
      await onResetPassword(email, code, password)
      setStep('success')
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  // ── Resend code ──
  async function handleResend() {
    setLoading(true)
    setError('')
    try {
      if (onResendCode) {
        await onResendCode(email)
      } else {
        await onSendCode(email)
      }
    } catch {
      // Silent
    } finally {
      setLoading(false)
    }
  }

  // ── Password strength meter ──
  const passwordStrength = (() => {
    if (password.length === 0) return null
    let score = 0
    if (password.length >= minPasswordLength) score++
    if (password.length >= minPasswordLength + 4) score++
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++
    if (score <= 2) return { label: 'Weak', color: 'bg-red-500', width: '33%' }
    if (score <= 3) return { label: 'Medium', color: 'bg-amber-500', width: '66%' }
    return { label: 'Strong', color: 'bg-green-500', width: '100%' }
  })()

  // ── Shared classes ──
  const inputCls = isDark
    ? 'w-full h-11 px-4 bg-[#0d1b35] border border-white/10 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20'
    : 'w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-all outline-none focus:border-[#1a3566] focus:ring-2 focus:ring-[#1a3566]/10'

  const labelCls = isDark
    ? 'block text-[11px] font-semibold text-white/40 uppercase tracking-widest mb-1.5'
    : 'block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5'

  const errorCls = isDark
    ? 'flex items-start gap-2.5 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm'
    : 'flex items-start gap-3 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-sm'

  const btnStyle = isDark
    ? { background: loading ? '#1e3566' : 'linear-gradient(135deg, #1a3566 0%, #2a52a0 100%)' }
    : { background: loading ? '#1a3566' : 'linear-gradient(135deg, #162952 0%, #1e3d78 100%)' }

  const leftPanelBg = isDark
    ? 'linear-gradient(155deg, #060e1f 0%, #0d1b35 45%, #111f3c 100%)'
    : 'linear-gradient(155deg, #0f1f3d 0%, #162952 40%, #1a3566 70%, #1e3d78 100%)'

  // ── Render ──
  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#0a1628]' : 'bg-white'}`}>
      {/* ── Left branding panel ── */}
      <div
        className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col relative overflow-hidden"
        style={{ background: leftPanelBg }}
      >
        {/* Grid overlay */}
        <div
          className={`absolute inset-0 ${isDark ? 'opacity-[0.06]' : 'opacity-[0.04]'}`}
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        {/* Glow blobs */}
        <div className="absolute top-[-80px] left-[-80px] w-[360px] h-[360px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #4d7cfe 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-60px] right-[-60px] w-[300px] h-[300px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #3eb8fa 0%, transparent 70%)' }} />
        <div className="absolute top-[45%] right-[10%] w-[180px] h-[180px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full px-12 py-10">
          {/* Logo */}
          <div className="flex items-center">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={logoAlt || portalName}
                className={`h-8 object-contain ${isDark ? 'brightness-0 invert opacity-90' : 'brightness-0 invert'}`}
              />
            )}
          </div>

          {/* Hero */}
          <div className="flex-1 flex flex-col justify-center mt-16 max-w-sm">
            {brandingBadge && (
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 mb-6 w-fit">
                {brandingBadgeIcon || <KeyRound className="w-3.5 h-3.5 text-blue-400" />}
                <span className="text-[11px] font-semibold text-blue-300 tracking-wide uppercase">{brandingBadge}</span>
              </div>
            )}
            {brandingHeadline || (
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-[1.15] mb-6">
                Reset your<br />password<br />
                <span style={{ color: '#7eb3ff' }}>securely.</span>
              </h1>
            )}
            {brandingSubtitle && (
              <p className="text-[15px] text-blue-100/70 leading-relaxed">{brandingSubtitle}</p>
            )}
          </div>

          {/* Footer strip */}
          <div className="flex gap-8 pb-4">
            {[
              { value: 'Secure', label: 'End-to-end encrypted' },
              { value: '6-digit code', label: 'Emailed to you' },
              { value: 'Quick', label: 'Reset in seconds' },
            ].map((s) => (
              <div key={s.label}>
                <p className={`font-semibold text-xs ${isDark ? 'text-white/80' : 'text-white'}`}>{s.value}</p>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-white/30' : 'text-blue-200/50'}`}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className={`flex-1 flex flex-col items-center justify-center px-6 sm:px-12 ${isDark ? 'bg-[#0c1528]' : 'bg-[#f8f9fc]'}`}>
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center mb-10">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={logoAlt || portalName}
              className={`h-7 object-contain ${isDark ? 'brightness-0 invert opacity-90' : ''}`}
            />
          )}
        </div>

        <div className="w-full max-w-[400px]">

          {/* ── Step 1: Enter email ── */}
          {step === 'email' && (
            <>
              <div className="mb-8">
                <h2 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Reset Password
                </h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  Enter your email and we'll send you a verification code
                </p>
              </div>

              <form onSubmit={handleSendCode} className="space-y-5">
                <div>
                  <label className={labelCls}>Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={emailPlaceholder}
                    autoComplete="email"
                    className={inputCls}
                    autoFocus
                    required
                  />
                </div>

                {error && (
                  <div className={errorCls}>
                    <svg className="mt-0.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                  A 6-digit verification code will be sent to your email.
                </p>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-11 rounded-xl text-sm font-semibold text-white shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={btnStyle}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(loginUrl)}
                  className={`w-full h-10 rounded-xl text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  ← Back to Login
                </button>
              </form>
            </>
          )}

          {/* ── Step 2: Enter code + new password ── */}
          {step === 'code' && (
            <>
              <div className="mb-6">
                <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-4 ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
                  <Mail className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Code Sent</span>
                </div>
                <h2 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Enter Verification Code
                </h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  Check <strong className={isDark ? 'text-white/70' : 'text-gray-700'}>{email}</strong> for a 6-digit code
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                {/* Code input */}
                <div>
                  <label className={labelCls}>Verification Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`}
                    autoFocus
                  />
                </div>

                {/* New Password */}
                <div>
                  <label className={labelCls}>New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={`At least ${minPasswordLength} characters`}
                      className={`${inputCls} pr-11`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-slate-600 hover:text-slate-400' : 'text-gray-400 hover:text-gray-600'}`}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordStrength && (
                    <div className="mt-2">
                      <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                        <div
                          className={`h-full rounded-full transition-all ${passwordStrength.color}`}
                          style={{ width: passwordStrength.width }}
                        />
                      </div>
                      <p className={`text-xs mt-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Strength: {passwordStrength.label}</p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className={labelCls}>Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    className={inputCls}
                  />
                </div>

                {error && (
                  <div className={errorCls}>
                    <svg className="mt-0.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !code || !password || !confirmPassword}
                  className="w-full h-11 rounded-xl text-sm font-semibold text-white shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={btnStyle}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Resetting…
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setStep('email'); setCode(''); setPassword(''); setConfirmPassword(''); setError('') }}
                    className={`flex-1 h-10 rounded-xl text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className={`flex-1 h-10 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-[#1a3566] hover:text-[#162952]'}`}
                  >
                    Resend Code
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Step 3: Success ── */}
          {step === 'success' && (
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${isDark ? 'bg-green-500/10' : 'bg-green-50 border border-green-200'}`}>
                <CheckCircle2 className={`w-8 h-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <h2 className={`text-2xl font-bold tracking-tight mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Password Reset!
              </h2>
              <p className={`text-sm mb-8 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                Your password has been updated successfully. You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate(loginUrl)}
                className="w-full h-11 rounded-xl text-sm font-semibold text-white shadow-sm transition-all flex items-center justify-center gap-2"
                style={btnStyle}
              >
                Go to Login
              </button>
            </div>
          )}

          {/* Footer note */}
          {footerNote && (
            <p className={`mt-6 text-center text-[11px] ${isDark ? 'text-white/20' : 'text-gray-400'}`}>
              {footerNote}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
